import {waitForMe} from './Utils';

let currentToken: string | null = null;
let currentTokenExpiry: Date | null = null;
let defaultHeaders: {[key: string]: string} = {};
let getIdToken: ((forceRefresh?: boolean) => Promise<string>) | null = null;

// Auth-ready gate. On hard page loads the web UI restores a logged-in user
// from localStorage instantly while Firebase takes ~a second to wire the
// token getter; secured calls made in that window used to fail outright and
// were never retried (notification bell / credits chip stayed empty).
// Callers now wait here, capped so a genuine wiring bug still surfaces.
// The tri-state below tells a KNOWN signed-out session ('signedOut', wiring
// ran and reported no user) apart from 'unknown' (wiring hasn't run yet).
// Signed-out callers get a short grace — covering a secured call issued
// moments before onAuthStateChanged flips the state on login — then fail
// fast with a named SignedOutError instead of hanging the full 15s and
// throwing the wiring error below (which now specifically means "wiring
// never happened", a real bug signal).
const AUTH_READY_TIMEOUT_MS = 15000;
const SIGNED_OUT_GRACE_MS = 2500;
let authState: 'unknown' | 'signedIn' | 'signedOut' = 'unknown';
let authReadyResolve: (() => void) | null = null;
let authReadyPromise: Promise<void> = new Promise((resolve) => {
  authReadyResolve = resolve;
});

const backoffMs: number[] = [500, 1000, 2000];

// Per-attempt timeout. React Native's fetch has no default timeout, so a hung
// TCP connection would otherwise block indefinitely and the app stays on the
// splash screen forever.
const DEFAULT_TIMEOUT_MS = 20000;

// Standard RequestInit plus two OPTIONAL per-call overrides. They only change
// the request they are passed to — the global 20s timeout and 3-retry backoff
// schedule remain the default for every caller that omits them. The boot path
// uses these to fail fast (see AuthHooks) instead of stalling the splash screen
// for ~a minute on a cold/slow backend.
export type FetchRequestOptions = RequestInit & {
  // Per-attempt abort timeout in ms (default DEFAULT_TIMEOUT_MS).
  timeoutMs?: number;
  // Max auto-retries for idempotent GET/HEAD reads (default backoffMs.length).
  // Clamped to [0, backoffMs.length]; 0 disables the retry storm entirely.
  retries?: number;
};

export const resetSecuredFetchRequest = (
  fn: ((forceRefresh?: boolean) => Promise<string>) | null,
) => {
  console.log('resetSecuredFetchRequest');
  currentToken = null;
  currentTokenExpiry = null;
  getIdToken = fn;
  if (fn) {
    authState = 'signedIn';
    authReadyResolve?.();
  } else {
    authState = 'signedOut';
    // Logged out: close the gate again for the next session.
    authReadyPromise = new Promise((resolve) => {
      authReadyResolve = resolve;
    });
  }
};

export const setDefaultHeaders = (headers: {[key: string]: string}) => {
  defaultHeaders = headers;
};

const getFirebaseToken = async (url: string) => {
  if (!getIdToken && authState === 'signedOut') {
    console.log('[getFirebaseToken] Signed out; short grace for:', url);
    await Promise.race([authReadyPromise, waitForMe(SIGNED_OUT_GRACE_MS)]);
    if (!getIdToken) {
      console.log(
        '[getFirebaseToken] Skipping secured request while signed out:',
        url,
      );
      const signedOutError = new Error(
        `Secured request skipped because no user is signed in: ${url}`,
      );
      signedOutError.name = 'SignedOutError';
      throw signedOutError;
    }
  }
  if (!getIdToken) {
    console.log('[getFirebaseToken] Waiting for auth wiring for:', url);
    await Promise.race([authReadyPromise, waitForMe(AUTH_READY_TIMEOUT_MS)]);
  }
  if (!getIdToken) {
    console.error('[getFirebaseToken] No getIdToken function set for:', url);
    throw Error(
      'You must provide a getIdToken via the `resetSecuredFetchRequest` function',
    );
  }
  if (
    !currentToken ||
    !currentTokenExpiry ||
    Date.now() > currentTokenExpiry.getTime()
  ) {
    console.log('[getFirebaseToken] Creating new token for:', url);
    try {
      currentToken = await getIdToken(true);
      currentTokenExpiry = new Date(Date.now() + 1000 * 60 * 60);
      console.log(
        '[getFirebaseToken] Token obtained, length:',
        currentToken?.length,
      );
    } catch (err) {
      console.error('[getFirebaseToken] Failed to get token:', err);
      throw err;
    }
  } else {
    console.log('[getFirebaseToken] Reusing cached token for:', url);
  }
  return currentToken;
};

/**
 * Returns Fetch() instance
 *
 * @param url {String}
 * @param options {Object}
 */

const noLogs = [
  'https://beta-api.bidmii.com/v1.1/users/online',
  'https://beta-api.bidmii.com/v1.1/my/notifications',
];

export const fetchRequest = async (
  url: string,
  options: FetchRequestOptions = {},
) => {
  let retries = 0;
  const requestStart = Date.now();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  // Per-call retry budget. Defaults to the full backoff schedule; the boot path
  // passes a smaller number (0/1) so a cold backend can't turn one slow request
  // into a ~minute-long retry storm on the splash screen. Clamped so the
  // default and the backoff-array index both stay in bounds.
  const maxRetries =
    options.retries != null
      ? Math.min(Math.max(0, options.retries), backoffMs.length)
      : backoffMs.length;
  // Only reads may be auto-retried. A timed-out POST/PUT/PATCH/DELETE usually
  // still completes server-side after we hang up, so re-sending it duplicates
  // the action (double charges, double records).
  const method = (options.method || 'GET').toUpperCase();
  const retryAllowed = method === 'GET' || method === 'HEAD';
  const executeRequest = async (): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      console.log(`[fetchRequest] Sending ${options.method || 'GET'} ${url}`);
      const res = await fetch(url, {...options, signal: controller.signal});
      console.log(
        `[fetchRequest] Response ${res.status} for ${options.method || 'GET'} ${url} in ${Date.now() - requestStart}ms`,
      );
      if (!res.ok) {
        // Try to read error response body for better error messages
        let errorMessage = `${res.status} ${res.statusText}`;
        let errorDetails: any = null;
        try {
          const errorText = await res.clone().text();
          if (errorText && errorText.trim()) {
            try {
              const errorJson = JSON.parse(errorText);
              // Try multiple possible error message fields
              errorMessage =
                errorJson.details?.detail ||
                errorJson.details?.message ||
                errorJson.message ||
                errorJson.error ||
                errorJson.detail ||
                errorText.trim() ||
                errorMessage;
              errorDetails = errorJson;
            } catch {
              // Not JSON, use the text directly if it's meaningful
              const trimmedText = errorText.trim();
              if (
                trimmedText &&
                trimmedText !== `${res.status}` &&
                trimmedText !== `${res.status} ${res.statusText}`
              ) {
                errorMessage = trimmedText;
              }
            }
          }
        } catch (e) {
          // If we can't read the response body, use the status text
          console.log('[fetchRequest] Could not read error response body:', e);
        }
        const error = new Error(
          errorMessage.trim() || `${res.status} ${res.statusText}`,
        );
        (error as any).status = res.status;
        (error as any).statusText = res.statusText;
        (error as any).errorDetails = errorDetails;
        throw error;
      }
      return res;
    } catch (err: any) {
      const failedToFetch =
        err?.message && err.message.includes('Failed to fetch');
      const timedOut = err?.name === 'AbortError';
      if (timedOut) {
        const timeoutErr = new Error(
          `Request timed out after ${timeoutMs}ms: ${options.method || 'GET'} ${url}`,
        );
        (timeoutErr as any).name = 'TimeoutError';
        err = timeoutErr;
      }

      if (
        retryAllowed &&
        retries < maxRetries &&
        (failedToFetch === true || timedOut === true)
      ) {
        console.log(
          '[fetchRequest] error and retry',
          retries,
          backoffMs[retries],
          err,
        );
        await waitForMe(backoffMs[retries]);
        retries++;
        return await executeRequest();
      } else {
        console.log('[fetchRequest] error', url, ':>>', err);
        throw err;
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  if (!noLogs.includes(url)) {
    if (url.indexOf('v1.2/') === -1) {
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error(`MISSING V1.2 PREFIX: ${options.method || 'GET'} ${url}`);
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
      console.error('DANGER DANGER DANGER DANGER');
    } else {
      console.log(`${options.method || 'GET'} ${url}`);
    }
  }
  options.headers = {
    ...(options.headers || {}),
    ...defaultHeaders,
  };

  return await executeRequest();
};

export const securedFetchRequest = async (
  url: string,
  options: FetchRequestOptions = {},
) => {
  const startTime = Date.now();
  console.log(`[securedFetch] START ${options.method || 'GET'} ${url}`);
  try {
    const token = await getFirebaseToken(url);
    console.log(
      `[securedFetch] Token ready after ${Date.now() - startTime}ms, sending request...`,
    );
    options.headers = {
      ...(options.headers || {}),
      'Firebase-Authorization': token,
    };
    const res = await fetchRequest(url, options);
    console.log(
      `[securedFetch] DONE ${options.method || 'GET'} ${url} — ${res.status} in ${Date.now() - startTime}ms`,
    );
    return res;
  } catch (err) {
    console.error(
      `[securedFetch] FAILED ${options.method || 'GET'} ${url} after ${Date.now() - startTime}ms:`,
      err,
    );
    throw err;
  }
};
